<template>
  <AnalysisPage title="ダッシュボード" :busy="pdfBusy" noHeader>
    <div class="dashboard">
      <div class="dashboard-inner">
        <div class="header">
          <div class="month-select">
            <Button icon class="prev" :disabled="isMinMonth || busy" @click="onChangeMonth(2)">
              <ArrowLightIcon />
            </Button>
            <YearMonthSelectPopover v-model="yearMonth" :yearMonths="betweenDates" :scrollTo="10">
              <div class="year-month">
                <span class="year">{{ format(yearMonth, 'YYYY年') }}</span>
                <span class="month">{{ format(yearMonth, 'M月') }}</span>
              </div>
            </YearMonthSelectPopover>
            <Button icon class="next" :disabled="isThisMonth(yearMonth) || busy" @click="onChangeMonth(0)">
              <ArrowLightIcon />
            </Button>
          </div>
          <div class="timestamp">更新 : {{ timestamp }}</div>
          <Button flat :disabled="busy" class="download" @click="downloadPDF"><DownloadIcon />PDF</Button>
        </div>
        <ChartBox title="人数" target="customer" :yearMonth="yearMonth" full>
          <template #body>
            <div class="customer">
              <div v-if="hasTarget" class="target">
                <div class="target-head">
                  <div class="target-chart">
                    <Chart type="PieChart" :data="achievementData" :options="targetOption" />
                    <div v-if="!progress.busy" class="target-info">
                      <div class="label">目標達成率</div>
                      <div class="achievement">
                        {{ achievementRateNum.integer }}<span class="decimal">.{{ achievementRateNum.decimal }}</span
                        ><span class="rate">%</span>
                      </div>
                      <div class="diff">
                        <span class="operator">{{ toOperator(diffType) }}</span>
                        <span class="value">{{ numberFormat(Math.abs(diff)) }}</span>
                        人
                      </div>
                    </div>
                  </div>
                </div>
                <div class="target-body">
                  <div class="breakdown">
                    <dl>
                      <dt>目標</dt>
                      <dd>
                        <span class="value">{{ numberFormat(target) }}</span>
                        <span class="suffix">人</span>
                      </dd>
                    </dl>
                    <dl>
                      <dt>実績</dt>
                      <dd>
                        <span class="value">{{ numberFormat(achievement) }}</span>
                        <span class="suffix">人</span>
                      </dd>
                    </dl>
                    <span class="visited">来店済み{{ numberFormat(visited) }}人</span>
                  </div>
                  <hr />
                  <div class="compare">
                    <dl>
                      <dt>前月</dt>
                      <dd>
                        <FluctuationIcon :type="mom.fluctuationType" />
                        <span class="operator">{{ toOperator(mom.fluctuationType) }}</span>
                        <p class="value">{{ mom.fluctuationValue }}<span class="suffix">%</span></p>
                      </dd>
                    </dl>
                    <dl>
                      <dt>前年</dt>
                      <dd>
                        <FluctuationIcon :type="yoy.fluctuationType" />
                        <span class="operator">{{ toOperator(yoy.fluctuationType) }}</span>
                        <p class="value">{{ yoy.fluctuationValue }}<span class="suffix">%</span></p>
                      </dd>
                    </dl>
                  </div>
                </div>
                <div class="target-foot">
                  <Button auto small href="/analysis/target" :primary="!hasTarget">目標設定</Button>
                  <Button auto small :href="`/analysis/progress?endTerm=${yearMonth}`">達成状況</Button>
                </div>
              </div>
              <div v-else class="no-target">
                <div class="label">実績</div>
                <div class="value">{{ numberFormat(achievement) }}<span class="suffix">人</span></div>
                <span class="visited">来店済み{{ numberFormat(visited) }}人</span>
                <hr />
                <p class="catch">目標を設定して売上アップを目指そう！</p>
                <p v-if="isSP" class="note">iPadまたはPCから目標を設定してください</p>
                <Button href="/analysis/target" v-else auto small primary>目標設定</Button>
              </div>
              <hr />
              <div class="progress">
                <FrameTemplate :busy="busy" :nodata="!progressChart" class="frame">
                  <Chart
                    type="AreaChart"
                    :data="progressChart"
                    :options="{ ...progressColors, ...progressOptions() }"
                    :unit="2"
                  />
                  <template #v-axis-unit>人</template>
                  <template #h-axis-unit>日</template>
                </FrameTemplate>
                <div class="legends">
                  <div class="item circle sales"><span />実績（来店済み+予約受付）</div>
                  <div v-if="hasTarget" class="item dailytarget"><span />日別目標</div>
                  <div v-if="hasTarget" class="item monthlytarget"><span />月別目標</div>
                </div>
              </div>
            </div>
          </template>
        </ChartBox>
        <ChartBox
          title="予約経路"
          target="route"
          :detail="routeDetail"
          :data="routeChart"
          :options="chartBoxOptions(routeViewWindowMaxValue)"
          :busy="route.busy"
          :yearMonth="yearMonth"
        />
        <ChartBox
          title="コース"
          target="coursemenu"
          unit="5"
          :detail="courseDetail"
          :data="courseChart"
          :options="chartBoxOptions(courseViewWindowMaxValue)"
          :busy="course.busy"
          :yearMonth="yearMonth"
          note="注文数"
        />
        <ChartBox
          title="予約タグ"
          target="tag"
          :detail="tagDetail"
          :data="tagChart"
          :options="chartBoxOptions(tagViewWindowMaxValue)"
          :busy="tag.busy"
          :yearMonth="yearMonth"
        />
        <ChartBox
          title="キャンセル"
          target="cancel"
          :detail="cancelDetail"
          type="ColumnChart"
          :data="cancelChart"
          :options="cancelOption"
          :busy="cancel.busy"
          :yearMonth="yearMonth"
          isCancel
        />
      </div>
    </div>
  </AnalysisPage>
</template>

<script>
import * as JsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { debounce, isUndefined, isNull } from 'lodash'
import { mapState, mapGetters } from 'vuex'
import { format, isThisMonth, subMonths } from 'date-fns'
import { numberFormat } from '@/utils/number'
import { weekDay } from '@/utils/date'
import { toOperator } from '@/utils/analysis'
import Button from '@/components/atoms/Button'
import ArrowLightIcon from '@/components/icons/arrowLight'
import DownloadIcon from '@/components/icons/download'
import AnalysisPage from '@/components/templates/AnalysisPage'
import YearMonthSelectPopover from '@/components/molecules/YearMonthSelectPopover'
import Chart, { FrameTemplate } from '@/components/molecules/Chart'
import ChartBox from './Components/ChartBox'
import FluctuationIcon from './Components/FluctuationIcon'

export default {
  name: 'DashBoard',
  components: {
    Button,
    ArrowLightIcon,
    DownloadIcon,
    AnalysisPage,
    YearMonthSelectPopover,
    Chart,
    FrameTemplate,
    ChartBox,
    FluctuationIcon,
  },
  data() {
    return {
      date: new Date(),
      pdfBusy: false,
      windowSize: document.body.clientWidth,
      targetOption: {
        height: 'auto',
        chartArea: { width: '100%', height: '100%', left: 0, top: 0 },
        pieHole: 0.88,
        pieSliceText: 'none',
        pieSliceTextStyle: { color: 'black' },
        pieSliceBorderColor: 'transparent',
        enableInteractivity: false,
        legend: 'none',
        tooltip: { trigger: 'none' },
        colors: ['#47A3CC', '#E6E6E6'],
        slices: {
          0: { color: '#47A3CC' },
          1: { color: '#E6E6E6' },
        },
      },
    }
  },
  computed: {
    ...mapState({
      conditions: state => state.analysis.aggregate.conditions,
      betweenDates: state => state.analysis.aggregate.betweenDates,
      customer: state => state.analysis.customer,
      route: state => state.analysis.route,
      tag: state => state.analysis.tag,
      course: state => state.analysis.courseRanking,
      cancel: state => state.analysis.cancel,
      progress: state => state.analysis.progress,
      isSP: state => state.app.isSP,
    }),
    ...mapGetters({
      query: 'analysis/aggregate/query',
      progressChart: 'analysis/progress/chart',
      colors: 'analysis/colors/progress',
      routeChart: 'analysis/route/dashboardChart',
      routeDetail: 'analysis/route/detail',
      routeViewWindowMaxValue: 'analysis/route/viewWindowMaxValue',
      tagChart: 'analysis/tag/dashboardChart',
      tagDetail: 'analysis/tag/detail',
      tagViewWindowMaxValue: 'analysis/tag/viewWindowMaxValue',
      courseChart: 'analysis/courseRanking/dashboardChart',
      courseDetail: 'analysis/courseRanking/detail',
      courseViewWindowMaxValue: 'analysis/courseRanking/viewWindowMaxValue',
      cancelChart: 'analysis/cancel/dashboardChart',
      cancelDetail: 'analysis/cancel/detail',
      cancelViewWindowMaxValue: 'analysis/cancel/viewWindowMaxValue',
      chartBoxColors: 'analysis/colors/chartBox',
    }),
    yearMonth: {
      get() {
        return this.conditions.endTerm
      },
      set(value) {
        this.$store.commit('analysis/aggregate/update', { endTerm: value })
        this.fetch()
      },
    },
    timestamp() {
      const date = format(this.date, 'YYYY/MM/DD')
      const time = format(this.date, 'HH:mm')
      return `${date}（${weekDay(this.date)}）${time}`
    },
    achievementRateNum() {
      if (this.achievementRate === null) return this.achievementRate
      const [integer, decimal] = parseFloat(this.achievementRate ?? 0)
        .toFixed(1)
        .split('.')
      return { integer, decimal }
    },
    diff() {
      if (this.progress.summary === null) return 0
      return this.progress.summary.achievement - this.progress.summary.target
    },
    diffType() {
      switch (true) {
        case this.diff > 0:
          return 1
        case this.diff < 0:
          return 2
        default:
          return 0
      }
    },
    achievementRate() {
      return this.progress.summary?.achievementRate
    },
    target() {
      return this.progress.summary?.target ?? 0
    },
    achievement() {
      return this.progress.summary?.achievement ?? 0
    },
    visited() {
      return this.progress.summary?.visited ?? 0
    },
    mom() {
      return this.progress.summary?.mom ?? { fluctuationType: 0, fluctuationValue: 0 }
    },
    yoy() {
      return this.progress.summary?.yoy ?? { fluctuationType: 0, fluctuationValue: 0 }
    },
    achievementData() {
      const column = ['Target', 'value']
      const achievementRow = ['達成人数', this.achievement]
      const noncompletionRow = ['', this.diff > 0 ? 0 : Math.abs(this.diff)]
      return [column, achievementRow, noncompletionRow]
    },
    busy() {
      return this.customer.busy || this.route.busy || this.tag.busy || this.course.busy || this.cancel.busy
    },
    isMinMonth() {
      return this.yearMonth === '2018-09'
    },
    hasTarget() {
      return !isNull(this.achievementRate) && !isUndefined(this.achievementRate)
    },
    progressColors() {
      return { colors: this.colors }
    },
    cancelOption() {
      return {
        height: 292,
        chartArea: { width: '80%', height: '85%', left: '10%' },
        tooltip: { trigger: 'none' },
        bar: { groupWidth: 48 },
        hAxis: { baselineColor: '#ccc' },
        vAxis: { ticks: [], viewWindow: { max: this.cancelViewWindowMaxValue } },
        annotations: {
          textStyle: {
            fontSize: 16,
            bold: true,
            opacity: 0.6,
          },
          stem: { color: 'transparent', length: 4 },
          alwaysOutside: true,
        },
      }
    },
  },
  mounted() {
    this.$store.dispatch('analysis/aggregate/start', { defaultUnit: null })
    this.fetch()
    window.addEventListener('resize', this.windowResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.windowResize)
  },
  methods: {
    format,
    isThisMonth,
    numberFormat,
    toOperator,
    async fetch() {
      const { dispatch } = this.$store
      const dashboardQuery = {
        ...this.query,
        unit: 4,
        dashboardFlg: 1,
      }
      await Promise.all([
        dispatch('analysis/progress/get', dashboardQuery),
        dispatch('analysis/route/get', dashboardQuery),
        dispatch('analysis/customer/get', dashboardQuery),
        dispatch('analysis/tag/get', dashboardQuery),
        dispatch('analysis/courseRanking/get', dashboardQuery),
        dispatch('analysis/cancel/get', dashboardQuery),
      ])
      this.date = new Date()
    },
    windowResize: debounce(function onResize() {
      // eslint-disable-next-line no-invalid-this
      this.windowSize = document.body.clientWidth
    }, 200),
    onChangeMonth(value) {
      // Dateオブジェクトの month は 0始まりであることを考慮する
      const [year, month] = this.yearMonth.split('-')
      this.yearMonth = format(subMonths(new Date(year, month), value), 'YYYY-MM')
    },
    async downloadPDF() {
      this.pdfBusy = true
      await this.$nextTick()

      const target = this.$root.$el.querySelector('.dashboard-inner')

      // SafariではclientHeightが画面領域分しか取ってくれないためscrollHeightをベースにする
      const { clientWidth, scrollHeight } = target

      const canvas = await html2canvas(target, { width: clientWidth, height: scrollHeight })
      const dataURI = canvas.toDataURL()

      // jspdf初期化
      const pdf = new JsPDF()

      // サイズ調整
      const width = pdf.internal.pageSize.width
      pdf.internal.pageSize.height = (scrollHeight / clientWidth) * width
      pdf.addImage(dataURI, 'JPEG', 0, 0, width, 0, '', 'FAST')
      pdf.save(`ダッシュボード_${format(new Date(), 'YYYYMMDD')}.pdf`)

      this.pdfBusy = false
    },
    chartBoxOptions(viewWindowMaxValue) {
      return {
        height: 292,
        chartArea: { width: '70%', height: '100%', left: '30%', top: 0, bottom: 0 },
        legend: { position: 'none' },
        tooltip: { trigger: 'none' },
        hAxis: {
          viewWindow: { max: viewWindowMaxValue },
          gridlines: { color: '#F2F2F2' },
          minorGridlines: { color: 'transparent' },
          ticks: [],
          baselineColor: '#ccc',
        },
        vAxis: { ticks: [], maxTextLines: 1 },
        colors: [this.chartBoxColors.other],
        annotations: {
          textStyle: {
            fontSize: 16,
            color: this.chartBoxColors.other,
            bold: true,
            opacity: 0.6,
          },
          stem: { color: 'transparent', length: 4 },
          alwaysOutside: true,
        },
      }
    },
    progressOptions() {
      const showTextEvery = (() => {
        switch (true) {
          case this.windowSize < 640:
            return 6
          case this.windowSize < 1177:
            return 2
          default:
            return 1
        }
      })()
      return {
        height: this.isSP ? 218 : 295,
        annotations: {
          style: 'line',
          stem: { color: '#47A3CC' },
        },
        series: {
          0: { areaOpacity: 0.4 },
          1: { type: 'line', pointSize: 0 },
          2: { areaOpacity: 0.2, pointSize: 0 },
        },
        vAxis: { gridline: { count: 6 } },
        chartArea: {
          height: 284,
          right: this.isSP ? 5 : 0,
          top: 5,
          bottom: 22,
        },
        hAxis: { showTextEvery },
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.dashboard {
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.dashboard-inner {
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 32px;
}

.header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.month-select {
  background: var(--white);
  border-radius: 4px;
  box-sizing: border-box;
  display: flex;
  height: 40px;
  justify-content: space-between;
  padding: 0 12px;
  width: 288px;

  > .prev {
    transform: rotate(90deg);
  }

  > .next {
    transform: rotate(-90deg);
  }
}

.year-month {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-gap: 8px;
  line-height: 24px;
  padding: 8px 0;
  position: relative;
  &::after {
    border-bottom: 5px solid transparent;
    border-left: 5px solid #757575;
    border-top: 5px solid transparent;
    content: '';
    height: 0;
    margin: 0 7px;
    pointer-events: none;
    transform: rotate(90deg);
    width: 0;
  }
}

.month {
  font-size: 21px;
  font-weight: 600;
}

.timestamp {
  font-size: 10px;
  margin: 0 auto 0 16px;
}

.download {
  font-size: 15px;
  padding: 8px 16px;
  position: relative;
  &::before {
    border-left: 1px solid var(--border-color);
    bottom: 0;
    content: '';
    display: inline-block;
    height: 40px;
    left: 0;
    margin: auto 0;
    position: absolute;
    top: 0;
  }
}

hr {
  background: var(--border-color);
  border: 0;
  margin: 0;
}

.customer {
  display: flex;
  padding-top: 8px;

  > hr {
    margin: 0 8px;
  }
}

.target {
  width: 224px;
}

.target-head {
  padding: 0 12px;
}

.target-chart {
  box-sizing: border-box;
  padding-bottom: 100%;
  position: relative;

  >>> .chart {
    height: 100%;
    position: absolute;
    width: 100%;
  }
}

.target-info {
  align-items: center;
  bottom: 0;
  display: grid;
  grid-template-rows: 16px 1fr 24px;
  height: 95px;
  justify-content: center;
  left: 0;
  margin: auto;
  position: absolute;
  right: 0;
  text-align: center;
  top: 0;
  width: 140px;

  .label {
    margin: 0 auto;
    width: 72px;
  }

  .achievement {
    font-size: 38px;
    font-weight: 600;
    line-height: 1;
  }

  .decimal {
    font-size: 24px;
  }

  .rate {
    font-size: 21px;
  }

  .diff {
    border-bottom: solid 1px var(--border-color);
    border-top: solid 1px var(--border-color);
    font-size: 11px;
    font-weight: bold;
    line-height: 1;
    padding: 4px 0;
    width: 112px;
  }

  .operator {
    font-size: 14px;
  }

  .value {
    font-size: 16px;
  }
}

.visited {
  align-items: baseline;
  display: grid;
  font-size: 10px;
  grid-auto-flow: column;
  grid-gap: 2px;
  line-height: 12px;
  width: fit-content;

  &::before {
    color: var(--light-gray);
    content: '[';
    font-size: 12px;
  }
  &::after {
    color: var(--light-gray);
    content: ']';
    font-size: 12px;
  }
}

.no-target {
  text-align: center;
  width: 224px;

  .label {
    padding: 0;
    width: 28px;
  }

  .value {
    font-size: 52px;
    font-weight: 600;
    line-height: 1;
    margin: 4px 0;
  }

  .suffix {
    font-size: 16px;
    font-weight: 600;
  }

  .visited {
    margin: 0 auto;
  }

  hr {
    height: 1px;
    margin: 16px 0;
  }

  .catch {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.05em;
  }

  .note {
    text-align: center;
  }

  > .button {
    margin: 16px auto 0;
    width: 104px;
  }
}

.target-body {
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 1fr 1px 1fr;
  margin-top: 16px;
  position: relative;
  text-align: center;
}

.compare,
.breakdown {
  text-align: left;
  dl {
    align-items: center;
    display: grid;
    grid-template-columns: 24px 1fr;
    line-height: 16px;

    & + dl {
      margin-top: 8px;
    }
  }

  dt {
    font-size: 12px;
  }

  dd {
    align-items: center;
    font-weight: 600;
    text-align: left;
  }

  .value {
    font-size: 13px;
    font-weight: 600;
  }

  .suffix {
    font-size: 11px;
    font-weight: 600;
    margin: 0;
  }
}

.breakdown {
  dd {
    display: flex;
  }

  .visited {
    margin-top: 4px;
  }
}

.compare {
  dd {
    display: grid;
    grid-template-columns: 16px 12px 1fr;
  }

  >>> svg {
    height: 16px;
    width: 16px;
  }

  .operator {
    font-size: 11px;
    text-align: center;
    width: 12px;
  }
}

.target-foot {
  display: grid;
  grid-auto-flow: column;
  grid-gap: 16px;
  justify-content: center;
  margin-top: 16px;

  > .button {
    width: 104px;
  }
}

.progress {
  flex: 1 1 auto;

  >>> .layout {
    grid-template-columns: 48px 1fr 1em;
    grid-template-rows: 18px 1fr 12px;
    height: 325px;
  }

  .legends {
    display: flex;
    font-size: 10px;
    justify-content: flex-end;

    .item {
      line-height: 1;
      & span {
        display: inline-block;
        height: 8px;
        margin-right: 8px;
        width: 8px;
      }

      &.circle span {
        border-radius: 50%;
      }

      /** TODO 色整理 */
      &.sales span {
        background-color: var(--light-blue-1);
      }
      &.dailytarget span {
        background-color: var(--very-light-yellow);
      }
      &.monthlytarget span {
        background-color: var(--moderate-orange);
      }
    }

    .item + .item {
      margin-left: 16px;
    }
  }
}

.responsive {
  .dashboard-inner {
    padding: 0;
  }

  .header {
    flex-wrap: wrap;
  }

  .month-select {
    height: auto;
    margin-bottom: 8px;
    padding: 0 16px;
    width: 100%;
  }

  .year-month {
    padding: 16px 0;
  }

  .customer {
    flex-direction: column;

    > hr {
      background: var(--border-color);
      height: 1px;
      margin: 24px 0;
    }
  }

  .target {
    align-items: center;
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .target-head {
    flex: 1 1 auto;
    max-width: 223px;
    padding: 0;
  }

  .target-body {
    grid-template-columns: none;
    grid-template-rows: min-content 1px min-content;
    margin: 0 0 0 16px;
    min-width: 104px;
  }

  .target-foot {
    display: none;
  }

  .no-target {
    width: 100%;

    .value {
      font-size: 56px;
    }

    .visited {
      font-size: 10px;
    }

    hr {
      height: 0;
      margin: 8px 0;
    }

    .catch {
      font-size: 15px;
    }
  }

  .progress {
    >>> .layout {
      grid-template-columns: 48px 1fr 16px;
      height: 248px;
    }

    .legends {
      margin-right: 0;
    }
  }
}
</style>
